import React, { createContext, useContext, ReactNode } from 'react';
import { useAppSelector, useAppDispatch } from '@/hooks';
import {
  updateLoanSetup as updateLoanSetupAction,
  updateApplicationData as updateApplicationDataAction,
  setActiveStep as setActiveStepAction,
  setCompletedSteps as setCompletedStepsAction,
  clearAllLoanData,
  type LoanSetupData,
  type LoanApplicationData,
} from '@/store/slices/loanApplication.slice';

/**
 * Loan Context
 * Generic context for managing loan application data and progress
 * Supports multiple loan flows (QuickLoan, etc.)
 * Data is stored in Redux
 */

// Re-export types for backward compatibility
export type { LoanSetupData, LoanApplicationData };

interface LoanContextType {
  // Loan Setup
  loanSetupData: LoanSetupData;
  updateLoanSetup: (data: Partial<LoanSetupData>) => void;

  // Application Form
  applicationData: LoanApplicationData;
  updateApplicationData: (data: Partial<LoanApplicationData>) => void;

  // Progress
  activeStep: number;
  setActiveStep: (step: number) => void;
  completedSteps: number[];
  setCompletedSteps: (steps: number[]) => void;

  // Utility
  clearAllData: () => void;
  isLoading: boolean;
}

const LoanContext = createContext<LoanContextType | undefined>(undefined);

interface LoanProviderProps {
  children: ReactNode;
}

export const LoanProvider: React.FC<LoanProviderProps> = ({ children }) => {
  const dispatch = useAppDispatch();

  // Get data from Redux store
  const loanSetupData = useAppSelector(
    (state) => state.loanApplication.loanSetup
  );
  const applicationData = useAppSelector(
    (state) => state.loanApplication.application
  );

  const activeStep = applicationData.activeStep;
  const completedSteps = applicationData.completedSteps;

  // Update loan setup data
  const updateLoanSetup = (data: Partial<LoanSetupData>) => {
    dispatch(updateLoanSetupAction(data));
  };

  // Update application data
  const updateApplicationData = (data: Partial<LoanApplicationData>) => {
    dispatch(updateApplicationDataAction(data));
  };

  // Update active step
  const setActiveStep = (step: number) => {
    dispatch(setActiveStepAction(step));
  };

  // Update completed steps
  const setCompletedSteps = (steps: number[]) => {
    dispatch(setCompletedStepsAction(steps));
  };

  // Clear all data
  const clearAllData = () => {
    dispatch(clearAllLoanData());
  };

  return (
    <LoanContext.Provider
      value={{
        loanSetupData,
        updateLoanSetup,
        applicationData,
        updateApplicationData,
        activeStep,
        setActiveStep,
        completedSteps,
        setCompletedSteps,
        clearAllData,
        isLoading: false,
      }}
    >
      {children}
    </LoanContext.Provider>
  );
};

// Custom hook to use the LoanContext
export const useLoan = (): LoanContextType => {
  const context = useContext(LoanContext);
  if (!context) {
    throw new Error('useLoan must be used within a LoanProvider');
  }
  return context;
};

export default LoanContext;
